// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { sparseAssetSchema } from '@/asset/api/types';
import { z } from 'zod';

const validationErrorFieldSchema = z.object({
  fieldName: z.string(),
  error: z.string(),
  errorCode: z.number(),
});

const processedAssetCreationDataSchema = z.object({
  serial: z.string(),
  index: z.number(),
  created: z.boolean(),
  validatedAsset: sparseAssetSchema.nullable(),
  errors: z.array(validationErrorFieldSchema),
});

const rowValidationErrorSchema = z.object({
  index: z.number(),
  errors: z.array(validationErrorFieldSchema),
});

export const uploadAssetCreationResponseSchema = z.object({
  data: z.object({
    success: z.boolean(),
    items: z.array(processedAssetCreationDataSchema),
    structureErrors: z.array(validationErrorFieldSchema),
    rowErrors: z.array(rowValidationErrorSchema),
  }),
});

export type AssetCreationUploadResult = z.infer<
  typeof uploadAssetCreationResponseSchema
>['data'];

export async function uploadAssetCreation(
  formData: FormData
): Promise<AssetCreationUploadResult> {
  const url = '/v1/import/asset-creation';
  const response = await api.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  const parsed = await parseResponse(
    uploadAssetCreationResponseSchema,
    response.data,
    {
      schemaName: 'UploadAssetCreation',
      endpoint: url,
    }
  );
  return parsed.data;
}
